import { useState, useEffect } from 'react';
import {
  useQueryState as nextUseQueryState,
  useQueryStates as nextUseQueryStates,
  queryTypes,
  UseQueryStateOptions,
  UseQueryStateReturn,
  UseQueryStatesReturn,
  UseQueryStatesKeysMap,
  Values,
} from 'next-usequerystate';

// Library has a server match bug, wrapping the library here until they fix it.
function useQueryState<T>(
  key: string,
  options: UseQueryStateOptions<T>,
): UseQueryStateReturn<T & {}, undefined> {
  const [queryState, setQueryState] = nextUseQueryState<T>(key, options);
  const [isFirstRender, setIsFirstRender] = useState(true);
  useEffect(() => {
    setIsFirstRender(false);
  }, []);
  const state = isFirstRender ? null : queryState;
  return [state, setQueryState];
}

function useQueryStates<T extends UseQueryStatesKeysMap>(
  keys: T,
): UseQueryStatesReturn<T> {
  const [queryState, setQueryState] = nextUseQueryStates<T>(keys);
  const [isFirstRender, setIsFirstRender] = useState(true);
  useEffect(() => {
    setIsFirstRender(false);
  }, []);
  const state = isFirstRender
    ? (Object.keys(keys?.[0] || {}).reduce((obj, key) => {
        // `!`: `keys` maps a query-param name to its parser, so `keys[0]` is
        // `undefined` and the `Object.keys({})` above leaves this reduce with
        // nothing to iterate — the first render resolves to `{}`. Seeding real
        // defaults here would change what every caller reads on first render.
        // See docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
        const { defaultValue } = keys[0]!;
        return {
          ...obj,
          [key]: defaultValue ?? null,
        };
      }, {}) as Values<T>)
    : queryState;
  return [state, setQueryState];
}

export { queryTypes, useQueryState, useQueryStates };
